import { MutOpt } from '@/app/api';
import { queryClient } from '@/lib/query-client';
import { createQueryKeys } from '@/lib/query-factory';
import { List, ListEvent } from '@certhon/models';
import { MutationOptions, queryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from './lists.api';
import { ChecklistFilter } from '@/features/lists/lists.hooks';

const keys = createQueryKeys('checklists');

export const listQueries = {
  keys,

  list: (filters?: ChecklistFilter) =>
    queryOptions({
      queryKey: keys.list(filters),
      queryFn: () => api.fetchChecklists(filters),
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: keys.detail(id),
      queryFn: () => api.fetchChecklist(id),
      enabled: !!id,
    }),

  workChecklists: (workId: number) =>
    queryOptions({
      queryKey: [...keys.lists(), 'work', workId],
      queryFn: () => api.fetchChecklists({ werk_id: `eq.${workId}` }),
      enabled: !!workId,
    }),
};

export const listMutations = {
  postEvents: (): MutationOptions<List[], Error, ListEvent[]> => ({
    mutationFn: api.postListEvents,
    onSuccess: (lists) => {
      // Blunt: invalidate every cached list query (summary + detail).
      // Editing a template needs to refresh any checklist/template that
      // EXPAND_PROTOs it; tracking dependants here won't scale to the
      // planned offline-first model. Revisit per-key invalidation once
      // the local store is the source of truth.
      queryClient.invalidateQueries({ queryKey: keys.all });

      if (lists.length === 1) {
        toast.success('List updated');
      } else {
        toast.success(`${lists.length} lists updated`);
      }
    },
    onError: (error: Error) => {
      toast.error('List update failed', {
        description: error.message,
      });
    },
  }),

  delete: (): MutOpt<typeof api.deleteList> => ({
    mutationFn: api.deleteList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      toast.success('Deleted');
    },
    onError: (error: Error) => {
      toast.error('Delete failed', {
        description: error.message,
      });
    },
  }),
};
